import { useEffect, useState, useRef } from 'react';
import * as api from '../api';

// Banner colour options exposed in the Create/Edit Folder picker.
// Ordered to match the Fort Hill brand palette card (Indigo, Deep Grey,
// Old Mint, Ash Grey, Bone). Class names are kept from the original
// palette so existing folders in the DB keep rendering unchanged.
const FOLDER_COLORS = ['fc-teal', 'fc-blue', 'fc-amber', 'fc-coral', 'fc-green'];
const FOLDER_COLOR_SWATCHES = {
  'fc-teal':   '#184965',  // Indigo Dye
  'fc-blue':   '#262129',  // Deep Grey
  'fc-amber':  '#5fc2ab',  // Old Mint
  'fc-coral':  '#ccdbd2',  // Ash Grey
  'fc-green':  '#f6faf6',  // Bone
};
const FOLDER_ICONS = ['📁', '📂', '🗂️', '📚', '🎓', '🏢', '📋', '🔐', '💼', '🧩', '👥', '🏗️', '🛡️'];

export default function VideosPage({ user }) {
  const [folders, setFolders] = useState([]);
  const [rows, setRows] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [myProgress, setMyProgress] = useState({}); // { [video_id]: { status, watch_seconds } }
  const [err, setErr] = useState('');
  const [openFolder, setOpenFolder] = useState(null);

  const [videoModal, setVideoModal] = useState(false);
  const [videoForm, setVideoForm] = useState({ title: '', duration: '', timeLimit: 30, file: null });
  const [videoFormErr, setVideoFormErr] = useState('');

  const [folderModal, setFolderModal] = useState(false);
  const [folderForm, setFolderForm] = useState({ name: '', description: '', icon: '📁', color: 'fc-teal' });
  const [folderFormErr, setFolderFormErr] = useState('');

  const [confirm, setConfirm] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');

  // Inline quiz-after-video state (employee)
  const [quizPrompt, setQuizPrompt] = useState(null); // the quiz to take
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizSubmitting, setQuizSubmitting] = useState(false);

  const fileRef = useRef();
  const videoElRef = useRef(null);
  const lastTickRef = useRef(0);
  const isAdmin = user?.role === 'admin';

  const quizForVideo = (videoId) => quizzes.find(q => q.video_id === videoId);

  const load = async () => {
    try {
      const [f, v, q] = await Promise.all([api.getFolders(), api.getVideos(), api.getQuizzes()]);
      setFolders(f.data || []);
      setRows(v.data || []);
      setQuizzes(q.data || []);
      if (!isAdmin) {
        try {
          const mp = await api.getMyVideoProgress();
          const map = {};
          (mp.data || []).forEach(p => { map[p.video_id] = p; });
          setMyProgress(map);
        } catch { /* ignore — progress is optional */ }
      }
    } catch (e) { setErr(e.response?.data?.error || 'Could not load training content'); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const playVideo = async (video) => {
    setPlaying(video);
    setVideoUrl('');
    setQuizPrompt(null);
    setQuizResult(null);
    setQuizAnswers({});
    try {
      const { data } = await api.getVideoUrl(video.id);
      setVideoUrl(data.url);
    } catch {
      setVideoUrl('');
    }
  };

  const closePlayer = () => {
    setPlaying(null);
    setVideoUrl('');
    setQuizPrompt(null);
    setQuizResult(null);
    setQuizAnswers({});
    setQuizQuestions([]);
  };

  // — progress tracking while watching —
  const handleTimeUpdate = async (e) => {
    if (isAdmin || !playing) return;
    const t = Math.floor(e.target.currentTime || 0);
    if (t - lastTickRef.current < 10) return; // throttle to every ~10s
    lastTickRef.current = t;
    try {
      await api.updateProgress(playing.id, { status: 'in_progress', watch_seconds: t });
      setMyProgress(prev => ({ ...prev, [playing.id]: { ...(prev[playing.id] || {}), status: 'in_progress', watch_seconds: t } }));
    } catch { /* non-fatal */ }
  };

  const handleEnded = async () => {
    if (isAdmin || !playing) return;
    try {
      const dur = Math.floor(videoElRef.current?.duration || 0);
      await api.updateProgress(playing.id, { status: 'completed', watch_seconds: dur });
      setMyProgress(prev => ({ ...prev, [playing.id]: { status: 'completed', watch_seconds: dur, completed_at: new Date().toISOString() } }));
    } catch { /* non-fatal */ }

    // If a quiz is linked to this video, surface it inline inside the modal
    const linked = quizForVideo(playing.id);
    if (linked) {
      setQuizPrompt(linked);
      try {
        const { data } = await api.getQuizQuestions(linked.id);
        setQuizQuestions(data || []);
      } catch { setQuizQuestions([]); }
    }
  };

  const submitInlineQuiz = async () => {
    if (!quizPrompt) return;
    setQuizSubmitting(true);
    try {
      const { data } = await api.submitQuizAttempt(quizPrompt.id, quizAnswers);
      setQuizResult(data);
      load();
    } catch (e) { console.error(e); }
    finally { setQuizSubmitting(false); }
  };

  const handleUpload = async () => {
    setVideoFormErr('');
    if (!videoForm.title.trim()) { setVideoFormErr('Title is required.'); return; }
    if (!openFolder) { setVideoFormErr('No folder selected.'); return; }
    try {
      let s3Key = null;
      let fileSizeMb = null;

      if (videoForm.file) {
        try {
          const { data: presign } = await api.getPresignedUrl(videoForm.file.name, videoForm.file.type);
          await fetch(presign.url, { method: 'PUT', body: videoForm.file, headers: { 'Content-Type': videoForm.file.type } });
          s3Key = presign.key;
          fileSizeMb = +(videoForm.file.size / 1048576).toFixed(2);
        } catch {
          const fd = new FormData();
          fd.append('title', videoForm.title.trim());
          fd.append('folder_id', openFolder.id);
          fd.append('duration', videoForm.duration || '0:00');
          fd.append('duration_sec', '0');
          fd.append('time_limit_min', String(videoForm.timeLimit || 30));
          fd.append('file', videoForm.file);
          await api.uploadVideo(fd);
          setVideoForm({ title: '', duration: '', timeLimit: 30, file: null });
          setVideoModal(false);
          load();
          return;
        }
      }

      await api.createVideoRecord({
        title: videoForm.title.trim(),
        folder_id: openFolder.id,
        duration: videoForm.duration || '0:00',
        duration_sec: 0,
        time_limit_min: Number(videoForm.timeLimit) || 30,
        s3Key,
        fileSizeMb,
      });
      setVideoForm({ title: '', duration: '', timeLimit: 30, file: null });
      setVideoModal(false);
      load();
    } catch (e) { setVideoFormErr(e.response?.data?.error || 'Upload failed'); }
  };

  const handleDeleteVideo = async (id) => {
    try { await api.deleteVideo(id); setConfirm(null); load(); } catch (e) { console.error(e); }
  };

  const handleCreateFolder = async () => {
    setFolderFormErr('');
    if (!folderForm.name.trim()) { setFolderFormErr('Folder name is required.'); return; }
    try {
      await api.createFolder({
        name: folderForm.name.trim(),
        description: folderForm.description.trim() || null,
        icon: folderForm.icon,
        color: folderForm.color,
      });
      setFolderForm({ name: '', description: '', icon: '📁', color: 'fc-teal' });
      setFolderModal(false);
      load();
    } catch (e) { setFolderFormErr(e.response?.data?.error || 'Could not create folder'); }
  };

  const handleDeleteFolder = async (id) => {
    try {
      await api.deleteFolder(id);
      setConfirm(null);
      if (openFolder?.id === id) setOpenFolder(null);
      load();
    } catch (e) { console.error(e); }
  };

  const folderVideos = (fid) => rows.filter(v => v.folder_id === fid);

  // — folder-level completion % for employees (share completed) —
  const folderProgressPct = (fid) => {
    const fv = folderVideos(fid);
    if (isAdmin) {
      const total = fv.length;
      const published = fv.filter(v => v.status === 'published').length;
      return { pct: total ? (published / total) * 100 : 0, label: `${published} published` };
    }
    const total = fv.length;
    const done = fv.filter(v => myProgress[v.id]?.status === 'completed').length;
    return { pct: total ? (done / total) * 100 : 0, label: `${done} of ${total} completed` };
  };

  // — per-video watch % for an employee (best-effort from watch_seconds vs duration_sec) —
  const videoWatchPct = (v) => {
    const p = myProgress[v.id];
    if (!p) return 0;
    if (p.status === 'completed') return 100;
    const dur = v.duration_sec || 0;
    if (!dur) return p.status === 'in_progress' ? 15 : 0; // nominal indicator
    return Math.min(100, Math.round(((p.watch_seconds || 0) / dur) * 100));
  };

  if (err) return <div className="alert">{err}</div>;

  return (
    <div>
      {playing && (
        <div className="modal-overlay" onClick={closePlayer}>
          <div className="modal" style={{ width: 720 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{playing.title}</span>
              <button className="close-btn" onClick={closePlayer}>×</button>
            </div>

            {/* Step 1: the video (hide when quiz prompt active) */}
            {!quizPrompt && (
              <>
                {videoUrl ? (
                  <video
                    ref={videoElRef}
                    controls
                    autoPlay
                    style={{ width: '100%', borderRadius: 8, background: '#000' }}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleEnded}
                  >
                    <source src={videoUrl} type="video/mp4" />
                    Your browser does not support video playback.
                  </video>
                ) : (
                  <div className="video-thumb-fallback" style={{ height: 300, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center', color: 'white' }}>
                      <div style={{ fontSize: 48, marginBottom: 8 }}>🎬</div>
                      <div style={{ fontSize: 14 }}>{playing.file_path ? 'Loading video...' : 'No video file attached'}</div>
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {playing.duration} · Limit: {playing.time_limit_min} min
                    {!isAdmin && quizForVideo(playing.id) && ' · Quiz will unlock when the video ends'}
                  </span>
                  <span className="badge badge-teal">{playing.views} views</span>
                </div>
                {!isAdmin && quizForVideo(playing.id) && (
                  <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <button className="btn btn-secondary btn-sm" onClick={handleEnded}>
                      Skip to Quiz →
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Step 2: inline quiz once the video ends (employee only) */}
            {quizPrompt && !isAdmin && (
              <>
                {quizResult ? (
                  (() => {
                    const passed = quizResult.passed || quizResult.score >= quizPrompt.pass_score;
                    return (
                      <div style={{ textAlign: 'center', padding: '24px 8px' }}>
                        <div style={{ fontSize: 48, marginBottom: 12 }}>{passed ? '🎉' : '😔'}</div>
                        <div style={{ fontSize: 32, fontWeight: 800, color: passed ? 'var(--success)' : 'var(--danger)', marginBottom: 6 }}>{quizResult.score}%</div>
                        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                          {passed ? 'Nice work — you passed!' : 'Not quite — give it another try.'}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 20 }}>
                          {quizResult.correct} of {quizResult.total} correct · Required {quizPrompt.pass_score}%
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                          <button
                            className={passed ? 'btn btn-secondary' : 'btn btn-primary'}
                            onClick={() => { setQuizAnswers({}); setQuizResult(null); }}
                          >
                            {passed ? 'Retake for Practice' : 'Retake Quiz'}
                          </button>
                          <button className={passed ? 'btn btn-primary' : 'btn btn-secondary'} onClick={closePlayer}>Done</button>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <>
                    <div className="notification" style={{ marginBottom: 14 }}>
                      🎬 Video complete — here's your {quizPrompt.title}. ({quizQuestions.length} questions · Pass score {quizPrompt.pass_score}%)
                    </div>
                    {quizQuestions.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 24, color: 'var(--muted)' }}>This quiz has no questions yet.</div>
                    ) : (
                      <>
                        {quizQuestions.map((q, qi) => (
                          <div key={q.id} style={{ background: 'var(--bg)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Q{qi + 1}: {q.question_text}</div>
                            {['a', 'b', 'c', 'd'].map(opt => (
                              q['option_' + opt] && (
                                <div
                                  key={opt}
                                  className={`quiz-option ${quizAnswers[q.id] === opt ? 'selected' : ''}`}
                                  onClick={() => setQuizAnswers(prev => ({ ...prev, [q.id]: opt }))}
                                >
                                  <span style={{ marginRight: 8, fontWeight: 600 }}>{opt.toUpperCase()}.</span>
                                  {q['option_' + opt]}
                                </div>
                              )
                            ))}
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button className="btn btn-secondary" onClick={closePlayer}>Close</button>
                          <button
                            className="btn btn-primary"
                            style={{ flex: 1 }}
                            disabled={Object.keys(quizAnswers).length < quizQuestions.length || quizSubmitting}
                            onClick={submitInlineQuiz}
                          >
                            {quizSubmitting ? 'Submitting...' : `Submit Quiz (${Object.keys(quizAnswers).length}/${quizQuestions.length} answered)`}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {confirm && (
        <div className="modal-overlay" onClick={() => setConfirm(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="confirm-modal">
              <div className="confirm-icon">🗑️</div>
              <div className="confirm-title">{confirm.title}</div>
              <div className="confirm-sub">{confirm.message}</div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button className="btn btn-secondary" onClick={() => setConfirm(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={confirm.onConfirm}>Yes, Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!openFolder ? (
        <>
          <div className="section-header">
            <div>
              <div className="section-title">Training</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                {folders.length} folder{folders.length !== 1 ? 's' : ''} · {rows.length} total video{rows.length !== 1 ? 's' : ''}
              </div>
            </div>
            {isAdmin && (
              <button className="btn btn-primary" onClick={() => { setFolderFormErr(''); setFolderForm({ name: '', description: '', icon: '📁', color: 'fc-teal' }); setFolderModal(true); }}>+ New Folder</button>
            )}
          </div>

          {folders.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📁</div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>No folders yet</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>
                {isAdmin ? 'Create a folder to start organising your training videos' : 'Your admin has not added any training folders yet'}
              </div>
              {isAdmin && (
                <button className="btn btn-primary" onClick={() => { setFolderFormErr(''); setFolderModal(true); }}>Create First Folder</button>
              )}
            </div>
          ) : (
            <div className="folder-grid">
              {folders.map(f => {
                const fv = folderVideos(f.id);
                const total = fv.length;
                const { pct, label } = folderProgressPct(f.id);
                return (
                  <div key={f.id} className="folder-card" onClick={() => setOpenFolder(f)}>
                    <div className={`folder-banner ${f.color}`}>
                      <span style={{ fontSize: 40 }}>{f.icon}</span>
                      {isAdmin && (
                        <button className="folder-del"
                          onClick={e => {
                            e.stopPropagation();
                            setConfirm({
                              title: 'Delete Folder',
                              message: `Delete "${f.name}" and all ${total} video(s) inside? Linked assignments and quizzes will also be removed.`,
                              onConfirm: () => handleDeleteFolder(f.id),
                            });
                          }}>🗑️</button>
                      )}
                    </div>
                    <div className="folder-progress-bar">
                      <div className="folder-progress-fill" style={{ width: pct + '%' }}></div>
                    </div>
                    <div className="folder-body">
                      <div className="folder-name">{f.name}</div>
                      <div className="folder-meta">
                        <span>📹 {total} video{total !== 1 ? 's' : ''}</span>
                        <span>{isAdmin ? '✅' : '🎯'} {label}</span>
                      </div>
                      {f.description && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, lineHeight: 1.4 }}>{f.description}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {folderModal && (
            <div className="modal-overlay" onClick={() => setFolderModal(false)}>
              <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <span className="modal-title">Create New Folder</span>
                  <button className="close-btn" onClick={() => setFolderModal(false)}>×</button>
                </div>
                {folderFormErr && <div className="alert">{folderFormErr}</div>}
                <div className="form-group">
                  <label className="form-label">Folder Name *</label>
                  <input className="form-input" placeholder="e.g. Human Resources" value={folderForm.name} onChange={e => setFolderForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input className="form-input" placeholder="Brief description" value={folderForm.description} onChange={e => setFolderForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Folder Icon</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                      {FOLDER_ICONS.map(ic => (
                        <button key={ic} type="button" onClick={() => setFolderForm(f => ({ ...f, icon: ic }))}
                          style={{ width: 36, height: 36, borderRadius: 8, border: `2px solid ${folderForm.icon === ic ? 'var(--teal)' : 'var(--border)'}`, background: folderForm.icon === ic ? 'var(--teal-light)' : 'white', fontSize: 18, cursor: 'pointer' }}>
                          {ic}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Banner Colour</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                      {FOLDER_COLORS.map(c => (
                        <button
                          key={c}
                          type="button"
                          title={c.replace('fc-', '')}
                          onClick={() => setFolderForm(f => ({ ...f, color: c }))}
                          style={{
                            width: 36, height: 36, borderRadius: 8,
                            border: `3px solid ${folderForm.color === c ? 'var(--text)' : 'transparent'}`,
                            background: FOLDER_COLOR_SWATCHES[c],
                            // Always draw a hairline so very-light swatches (Ash, Bone) remain visible on the white modal.
                            boxShadow: 'inset 0 0 0 1px rgba(38,33,41,0.12)',
                            cursor: 'pointer',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ background: 'var(--bg)', borderRadius: 10, overflow: 'hidden', marginBottom: 16, border: '1px solid var(--border)' }}>
                  <div className={`folder-banner ${folderForm.color}`} style={{ height: 56, justifyContent: 'flex-start', padding: '0 16px', gap: 12 }}>
                    <span style={{ fontSize: 26 }}>{folderForm.icon}</span>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{folderForm.name || 'Folder Name'}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={() => setFolderModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleCreateFolder}>Create Folder</button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="breadcrumb">
            <span className="breadcrumb-link" onClick={() => setOpenFolder(null)}>📁 All Folders</span>
            <span style={{ color: 'var(--border)' }}>›</span>
            <span style={{ fontWeight: 600, color: 'var(--text)' }}>{openFolder.icon} {openFolder.name}</span>
          </div>
          <div className="section-header">
            <div>
              <div className="section-title">{openFolder.icon} {openFolder.name}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                {folderVideos(openFolder.id).length} video{folderVideos(openFolder.id).length !== 1 ? 's' : ''}
                {openFolder.description ? ` · ${openFolder.description}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setOpenFolder(null)}>← Back to Folders</button>
              {isAdmin && (
                <button className="btn btn-primary" onClick={() => { setVideoFormErr(''); setVideoForm({ title: '', duration: '', timeLimit: 30, file: null }); setVideoModal(true); }}>+ Upload Video</button>
              )}
            </div>
          </div>

          {folderVideos(openFolder.id).length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🎬</div>
              <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>No videos in this folder yet</div>
              <div style={{ fontSize: 13, marginBottom: 16 }}>{isAdmin ? 'Upload your first video' : 'Check back later'}</div>
              {isAdmin && (
                <button className="btn btn-primary" onClick={() => { setVideoFormErr(''); setVideoModal(true); }}>Upload Video</button>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              {folderVideos(openFolder.id).map(v => {
                const watchPct = videoWatchPct(v);
                const progStatus = myProgress[v.id]?.status;
                const hasQuiz = !!quizForVideo(v.id);
                return (
                  <div key={v.id} className="video-card">
                    <div className="video-thumb" onClick={() => playVideo(v)}>
                      <div className="play-btn">▶</div>
                      <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6 }}>
                        {isAdmin ? (
                          <span className={`badge ${v.status === 'published' ? 'badge-teal' : 'badge-amber'}`}>{v.status}</span>
                        ) : progStatus === 'completed' ? (
                          <span className="badge badge-green">✓ Completed</span>
                        ) : progStatus === 'in_progress' ? (
                          <span className="badge badge-amber">In progress</span>
                        ) : (
                          <span className="badge badge-blue">Not started</span>
                        )}
                        {hasQuiz && <span className="badge badge-teal" title="Quiz after video">❓ Quiz</span>}
                      </div>
                      {v.duration && (
                        <div style={{ position: 'absolute', bottom: 8, right: 8, fontSize: 12, color: 'rgba(255,255,255,0.9)', background: 'rgba(0,0,0,0.5)', padding: '2px 7px', borderRadius: 4 }}>
                          {v.duration}
                        </div>
                      )}
                      {isAdmin && (
                        <button onClick={e => {
                          e.stopPropagation();
                          setConfirm({
                            title: 'Delete Video',
                            message: `Delete "${v.title}"? This will also remove linked assignments and quizzes.`,
                            onConfirm: () => handleDeleteVideo(v.id),
                          });
                        }}
                          style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(217,72,72,0.85)', border: 'none', color: 'white', borderRadius: 5, padding: '3px 7px', cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                      )}
                    </div>
                    {!isAdmin && (
                      <div className="watch-progress" aria-label={`${watchPct}% watched`}>
                        <div
                          className={`watch-progress-fill ${progStatus === 'completed' ? 'completed' : ''}`}
                          style={{ width: watchPct + '%' }}
                        />
                      </div>
                    )}
                    <div className="video-meta">
                      <div className="video-title">{v.title || 'Untitled'}</div>
                      <div className="video-sub">
                        {v.file_size_mb && <span>{v.file_size_mb} MB</span>}
                        {v.file_size_mb && v.views != null && <span> · </span>}
                        {v.views != null && <span>{v.views} views</span>}
                      </div>
                      {!isAdmin && (
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                          {progStatus === 'completed' ? 'Completed' : `${watchPct}% watched`}
                        </div>
                      )}
                      {v.time_limit_min && (
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>Limit: {v.time_limit_min} min</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {videoModal && (
            <div className="modal-overlay" onClick={() => setVideoModal(false)}>
              <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <span className="modal-title">Upload Video to "{openFolder.name}"</span>
                  <button className="close-btn" onClick={() => setVideoModal(false)}>×</button>
                </div>
                {videoFormErr && <div className="alert">{videoFormErr}</div>}
                <div className="upload-zone" onClick={() => fileRef.current.click()} style={{ marginBottom: 16 }}>
                  <div className="upload-icon">🎬</div>
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>{videoForm.file ? videoForm.file.name : 'Click to browse or drag & drop'}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>MP4, MOV, AVI, MKV · Max 2GB</div>
                  <input ref={fileRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={e => setVideoForm(f => ({ ...f, file: e.target.files[0] }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Video Title *</label>
                  <input className="form-input" placeholder="e.g. HR Policies & Onboarding" value={videoForm.title} onChange={e => setVideoForm(f => ({ ...f, title: e.target.value }))} />
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Time Limit (min)</label>
                    <input className="form-input" type="number" min="1" value={videoForm.timeLimit} onChange={e => setVideoForm(f => ({ ...f, timeLimit: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Duration (mm:ss)</label>
                    <input className="form-input" placeholder="e.g. 18:30" value={videoForm.duration} onChange={e => setVideoForm(f => ({ ...f, duration: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={() => setVideoModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleUpload}>Upload</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
